import React, { useState, useRef, useCallback } from 'react';
import {
	IconButton,
	InputAdornment,
	Popover,
	Tooltip,
} from '@barracuda-internal/bds-core-v2';
import ClearIcon from '@mui/icons-material/Clear';
import Tune from '@barracuda-internal/bds-core-v2/dist/Icons/Core/Tune';
import type { CategoryGroup } from 'src/components/Filters/DIConditionCheckboxFilter';
import type { DetectionFilter } from 'src/features/detections/detectionSlice';
import FilterPopoverContent from './FilterPopoverContent';

interface SearchEndAdornmentProps {
	searchValue: string;
	onClear: () => void;
	currentSearchField: string;
	currentSearchText: string;
	currentFilters: DetectionFilter[];
	onApplyFilters: (
		searchField: string,
		searchText: string,
		filters: DetectionFilter[],
	) => void;
	classifierCategories: CategoryGroup[];
	searchBarRef?: React.RefObject<HTMLElement>;
	onSearchBarClickRef?: React.MutableRefObject<(() => void) | null>;
}

export const SearchEndAdornment: React.FC<SearchEndAdornmentProps> = ({
	searchValue,
	onClear,
	currentSearchField,
	currentSearchText,
	currentFilters,
	onApplyFilters,
	classifierCategories,
	searchBarRef,
	onSearchBarClickRef,
}) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const filterButtonRef = useRef<HTMLButtonElement>(null);

	const openPopover = useCallback(() => {
		const el = searchBarRef?.current || filterButtonRef.current;
		setAnchorEl(el);
	}, [searchBarRef]);

	const handleFilterClick = useCallback(() => {
		if (anchorEl) {
			setAnchorEl(null);
		} else {
			openPopover();
		}
	}, [anchorEl, openPopover]);

	// Expose openPopover to parent via ref so the search bar click can call it directly
	if (onSearchBarClickRef) {
		onSearchBarClickRef.current = () => {
			if (!anchorEl) {
				openPopover();
			}
		};
	}

	const handleFilterClose = () => {
		setAnchorEl(null);
	};

	const handleApply = (
		searchField: string,
		searchText: string,
		filters: DetectionFilter[],
	) => {
		onApplyFilters(searchField, searchText, filters);
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);

	const hasFilters = currentFilters.length > 0;
	const showTooltip = hasFilters && !!searchValue && !open;

	return (
		<>
			{/* Tooltip overlay covering the full input area — visible on hover when filters are active */}
			{showTooltip && (
				<Tooltip
					title={
						<span style={{ padding: '4px', display: 'block' }}>
							{searchValue}
						</span>
					}
					placement="bottom"
					arrow
					slotProps={{
						popper: {
							modifiers: [
								{
									name: 'offset',
									options: { offset: [0, -10] },
								},
							],
						},
					}}
				>
					<span
						onClick={() => {
							if (!anchorEl) openPopover();
						}}
						style={{
							position: 'absolute',
							left: 0,
							top: 0,
							width: '100%',
							height: '100%',
							zIndex: 1,
							cursor: 'pointer',
						}}
					/>
				</Tooltip>
			)}
			<InputAdornment position="end" sx={{ ml: 0, gap: '4px', zIndex: 2, position: 'relative' }}>
				{searchValue && (
					<IconButton
						aria-label="clear"
						bdsSize="medium"
						onClick={(e) => {
							e.stopPropagation();
							onClear();
						}}
					>
						<ClearIcon fontSize="inherit" />
					</IconButton>
				)}
				<Tooltip title="Filter">
					<IconButton
						ref={filterButtonRef}
						aria-label="filters"
						bdsSize="medium"
						onClick={(e) => {
							e.stopPropagation();
							handleFilterClick();
						}}
						sx={{
							color:
								open || currentFilters.length > 0
									? 'primary.main'
									: 'inherit',
						}}
					>
						<Tune fontSize="inherit" />
					</IconButton>
				</Tooltip>
			</InputAdornment>

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleFilterClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{
					paper: {
						sx: {
							mt: 1,
							p: 2,
							maxHeight: 500,
							overflowY: 'auto',
						},
					},
				}}
			>
				<FilterPopoverContent
					currentSearchField={currentSearchField}
					currentSearchText={currentSearchText}
					currentFilters={currentFilters}
					classifierCategories={classifierCategories}
					onApply={handleApply}
					onClose={handleFilterClose}
				/>
			</Popover>
		</>
	);
};
